import { FronteggContext } from '@frontegg/rest-api';
import { Axios, AxiosAmbient } from 'src/lib';
import {
  getAppointmentByFilterSlice,
  getAppointmentsByStatusSlice,
} from 'src/store';
import {
  childOrganizationsSlice,
  dotPhraseOverridesCreateLoadingSlice,
  dotPhraseOverridesDeleteLoadingSlice,
  dotPhraseOverridesListLoading,
  dotPhraseOverridesListSlice,
  dotPhraseOverridesUpdateLoadingSlice,
  practitionersByOrgsLoadingSlice,
  practitionersByOrgsSlice,
  selectedDotPhraseOverridesSlice,
  templateListLoading,
  templateListSlice,
  templateOverridesListLoading,
  templateOverridesListSlice,
  templateOverridesDeleteLoadingSlice,
  getEncounterCSVTimezoneSlice,
  getEncounterCSVTimezoneLoadingSlice,
  bulkEncounterUploadHistorySlice,
  bulkEncounterUploadHistoryLoadingSlice,
  templateAdminListSlice,
  templateAdminListLoadingSlice,
  templateAdminPlanListSlice,
  createEncountersOnBehalfOfSlice,
  createEncountersOnBehalfOfLoadingSlice,
} from '../slices/settings';
import { getOrganizationSettingsMappingConfig } from 'src/lib/requests/appointment.url';
import { getCurrentPractitionerSettings } from 'src/store/actions/practitioner.action';

import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { axiosAmbient } from '../../../../lib/axios';
import { logger } from 'src/services';

export const listTemplates = (encounterPhase, org_uuid = null) => {
  return async (dispatch) => {
    dispatch(templateListLoading(true));
    dispatch(getAppointmentsByStatusSlice([]));
    dispatch(getAppointmentByFilterSlice([]));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounterPhase,
    };
    if (org_uuid) {
      params.org_uuid = org_uuid;
    }
    try {
      const result = await AxiosAmbient.get(`/templates`, { headers, params });
      const response = result?.data;
      if (result?.status === 200) {
        dispatch(templateListSlice(response.templates));
      } else {
        showToastError('Failed to load templates');
      }
    } catch (e) {
      logger.error('Failed to load templates', { params }, e);
      showToastError('Failed to load templates');
    } finally {
      dispatch(templateListLoading(false));
    }
  };
};

export const listTemplateOverrides = (
  encounterPhase,
  all_templates = false
) => {
  return async (dispatch) => {
    dispatch(templateOverridesListLoading(true));
    dispatch(getAppointmentsByStatusSlice([]));
    dispatch(getAppointmentByFilterSlice([]));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounterPhase,
    };
    if (all_templates === true) {
      params.all = true;
    }
    try {
      const result = await AxiosAmbient.get(`/template_overrides`, {
        headers,
        params,
      });
      const response = result.data;
      dispatch(templateOverridesListSlice(response.templates));
    } catch (e) {
      logger.error('Failed to load template overrides', { params }, e);
      showToastError('Failed to load templates');
    } finally {
      dispatch(templateOverridesListLoading(false));
    }
  };
};

export const createTemplateOverride = (encounter_phase, data) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    try {
      const result = await AxiosAmbient.post(`/template_overrides`, data, {
        headers,
        params,
      });
      showToastSuccess('Template created successfully');
      await dispatch(listTemplateOverrides(encounter_phase));
      await dispatch(getCurrentPractitionerSettings());
      return result?.data;
    } catch (e) {
      showToastError('Failed to create template');
      throw e;
    }
  };
};

export const deleteTemplateOverride = (
  template_overrides_id,
  callBack = null
) => {
  return async (dispatch) => {
    try {
      dispatch(templateOverridesDeleteLoadingSlice(true));
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const params = {
        from_template_admin: 'true',
      };
      const result = await AxiosAmbient.delete(
        `/template_overrides/${template_overrides_id}`,
        { headers, params }
      );
      const response = result?.data;
      if (result?.status === 200) {
        showToastSuccess('Template deleted successfully');
        dispatch(listTemplateOverrides('', true));
        callBack?.();
      } else {
        showToastError('Failed to delete template');
      }
    } catch (error) {
      console.error(error);
      showToastError('Failed to delete template');
    } finally {
      dispatch(templateOverridesDeleteLoadingSlice(false));
    }
  };
};

export const updateTemplateOverride = (
  encounter_phase,
  template_overrides_id,
  data
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    try {
      const result = await AxiosAmbient.put(
        `/template_overrides/${template_overrides_id}`,
        data,
        { headers, params }
      );
      showToastSuccess('Template updated');
      await dispatch(listTemplateOverrides(encounter_phase));
      return result?.data;
    } catch (e) {
      showToastError('Failed to update template');
      throw e;
    }
  };
};

export const changePrebuiltTemplate = (
  encounter_phase,
  template_id,
  showToast = true
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const data = {
      template_id: template_id,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    const result = await AxiosAmbient.post(`/templates/change`, data, {
      headers,
      params,
    });
    const response = result?.data;
    if (result?.status === 200) {
      if (showToast) {
        showToastSuccess(`Template changed to ${response?.name}`);
      }
      dispatch(getCurrentPractitionerSettings());
    } else {
      if (showToast) {
        showToastError('Failed to update template');
      }
    }
  };
};

export const deleteCustomTemplateAction = (
  encounter_phase,
  template_overrides_id,
  setIsModalVisible = null,
  setWorkingTemplate,
  nextWorkingTemplate
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    await AxiosAmbient.delete(`/template_overrides/${template_overrides_id}`, {
      headers,
      params,
    })
      .then((response) => {
        if (response?.status === 200) {
          showToastSuccess('Template deleted successfully');
          dispatch(listTemplateOverrides(encounter_phase));
          dispatch(getCurrentPractitionerSettings());
          setWorkingTemplate(nextWorkingTemplate);
        } else {
          showToastError('Failed to delete');
        }
      })
      .catch((e) => {
        showToastError('Failed to delete');
      })
      .finally(() => {
        setIsModalVisible();
      });
  };
};

export const listDotPhraseOverrides = (options = { overrides: false }) => {
  return async (dispatch) => {
    dispatch(dotPhraseOverridesListLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.get(`/dot_phrase_overrides`, {
      headers,
      params: {
        overrides: options.overrides,
      },
    });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(dotPhraseOverridesListSlice(response.dot_phrases));
    } else {
      showToastError('Failed to load dot phrases');
    }

    dispatch(dotPhraseOverridesListLoading(false));
  };
};

export const createDotPhraseOverride = (data) => {
  return async (dispatch) => {
    dispatch(dotPhraseOverridesCreateLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.post(`/dot_phrase_overrides`, data, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 201) {
      dispatch(selectedDotPhraseOverridesSlice(response));
      dispatch(listDotPhraseOverrides());
    } else {
      showToastError('Failed to create dot phrase');
    }
    dispatch(dotPhraseOverridesCreateLoadingSlice(false));
  };
};

export const updateDotPhraseOverride = (template_overrides_id, data) => {
  return async (dispatch) => {
    dispatch(dotPhraseOverridesUpdateLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.put(
      `/dot_phrase_overrides/${template_overrides_id}`,
      data,
      { headers }
    );
    const response = result?.data;
    if (result?.status === 200) {
      showToastSuccess('Dot phrase updated');
      dispatch(selectedDotPhraseOverridesSlice(response));
      dispatch(listDotPhraseOverrides());
    } else {
      showToastError('Failed to update dot phrase');
    }
    dispatch(dotPhraseOverridesUpdateLoadingSlice(false));
  };
};

export const deleteDotPhraseOverride = (
  template_overrides_id,
  setSubmitLoading
) => {
  return async (dispatch) => {
    dispatch(dotPhraseOverridesDeleteLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.delete(
      `/dot_phrase_overrides/${template_overrides_id}`,
      { headers }
    );
    if (result?.status === 200) {
      showToastSuccess('Dot phrase deleted');
      dispatch(selectedDotPhraseOverridesSlice(null));
      dispatch(listDotPhraseOverrides());
      setSubmitLoading(false);
    } else {
      showToastError('Failed to delete dot phrase');
    }
    dispatch(dotPhraseOverridesDeleteLoadingSlice(false));
  };
};

export const deleteScribeDotPhraseOverride = (
  dot_phrase_overrides_id,
  callBack = null
) => {
  return async (dispatch) => {
    try {
      dispatch(dotPhraseOverridesDeleteLoadingSlice(true));
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const result = await AxiosAmbient.delete(
        `/dot_phrase_overrides/${dot_phrase_overrides_id}`,
        { headers }
      );
      const response = result?.data;
      if (result?.status === 200) {
        showToastSuccess('Dot phrase deleted successfully');
        dispatch(listDotPhraseOverrides({ overrides: true }));
        callBack?.();
      } else {
        showToastError('Failed to delete dot phrase');
      }
    } catch (error) {
      console.error(error);
      showToastError('Failed to delete dot phrase');
    } finally {
      dispatch(dotPhraseOverridesDeleteLoadingSlice(false));
    }
  };
};

export const getChildrenOrganizations = (parent_org_uuid) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await Axios.get(
      `/v2/organization_children?org_uuid=${parent_org_uuid}`,
      {
        headers,
      }
    );
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(childOrganizationsSlice(response));
    } else {
      showToastError('Failed to load sub-accounts');
    }
  };
};

export const getPractitionersByOrgs = (org_uuids) => {
  return async (dispatch) => {
    dispatch(practitionersByOrgsLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const payload = {
      org_uuids: org_uuids,
    };
    const result = await Axios.post(`/v2/practitioners_by_org_uuids`, payload, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(practitionersByOrgsSlice(response?.practitioners_data));
    } else {
      showToastError('Failed to load practitioners');
    }
    dispatch(practitionersByOrgsLoadingSlice(false));
  };
};

export const getEncounterCSVTimezone = () => {
  return async (dispatch) => {
    try {
      dispatch(getEncounterCSVTimezoneLoadingSlice(true));
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const result = await AxiosAmbient.get('/bulk_encounters/settings', {
        headers,
      });
      const response = result?.data;
      const timezone = response?.settings?.timezone;
      if (result?.status === 200 && timezone) {
        dispatch(getEncounterCSVTimezoneSlice(timezone));
      } else {
        dispatch(getEncounterCSVTimezoneSlice(null));
      }
    } catch (e) {
      dispatch(getEncounterCSVTimezoneSlice(null));
    } finally {
      dispatch(getEncounterCSVTimezoneLoadingSlice(false));
    }
  };
};

export const getBulkEncounterUploadHistory = (page, per_page) => {
  return async (dispatch) => {
    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    dispatch(bulkEncounterUploadHistoryLoadingSlice(true));
    dispatch(bulkEncounterUploadHistorySlice([]));
    try {
      const params = {
        page: page,
        per_page: per_page,
      };
      const result = await axiosAmbient.get('/bulk_encounters/history', {
        headers,
        params,
      });
      if (result?.status === 200) {
        dispatch(bulkEncounterUploadHistorySlice(result.data));
      } else {
        showToastError('Failed to get encounter upload history');
      }
    } catch (error) {
      console.error(error);
      showToastError(
        `Error getting encounter upload history: ${error.message}`
      );
    } finally {
      dispatch(bulkEncounterUploadHistoryLoadingSlice(false));
    }
  };
};
export const pushDotPhraseOverrides = (
  dot_phrase_overrides,
  orgs,
  practitionerMap,
  setSelectedOrganizations,
  setSelectedPractitioners,
  onSuccessCallback
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    let final_payload = {};
    for (const org_uuid of orgs) {
      if (practitionerMap[org_uuid]?.length > 0) {
        final_payload[org_uuid] = practitionerMap[org_uuid];
      } else {
        final_payload[org_uuid] = [];
      }
    }
    const payload = {
      dot_phrase_overrides_data: dot_phrase_overrides,
      org_practitioner_data: final_payload,
    };
    const result = await Axios.post(`/v2/assign_dot_phrases`, payload, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      showToastSuccess('Dot phrases pushed successfully');
      setSelectedOrganizations([]);
      setSelectedPractitioners([]);
      onSuccessCallback();
    } else {
      showToastError('Failed to push dot phrases');
    }
  };
};

export const getAllPreBuiltAdminTemplatesByPlan = () => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.get(`/templates/manage/plan`, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(templateAdminPlanListSlice(response.pre_built_templates));
    } else {
      showToastError('Failed to load templates');
    }
  };
};
export const getPreBuiltAdminTemplates = () => {
  return async (dispatch) => {
    dispatch(templateAdminListLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.get(`/templates/manage`, { headers });
    const response = result?.data;
    if (result?.status === 200) {
      const { template_mapping } = response;

      const templateMappingList = Object.entries(template_mapping).map(
        ([phase, phaseTemplates]) => ({
          encounter_phase: phase,
          templates: Object.entries(phaseTemplates)
            .filter(([_, template]) => template.enabled)
            .map(([template_id, { name }]) => ({
              template_id,
              name,
            })),
        })
      );

      dispatch(templateAdminListSlice(templateMappingList));
    } else {
      showToastError('Failed to load templates');
    }

    dispatch(templateAdminListLoadingSlice(false));
  };
};

export const putPreBuiltAdminTemplates = (
  template_mapping,
  encounter_phase
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const data = {
      template_mapping: template_mapping,
      encounter_phase: encounter_phase,
    };
    const result = await AxiosAmbient.put(`/templates/manage`, data, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      showToastSuccess('Templates updated successfully');
    } else {
      showToastError('Failed to update templates');
    }
  };
};

export const getSuperAdminPreBuiltAdminTemplates = (org_uuid) => {
  return async (dispatch) => {
    dispatch(templateAdminListLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      'Impersonate-Org-UUID': `${org_uuid}`,
      'Headless-Mode': true,
    };
    const result = await AxiosAmbient.get(`/superadmin/templates/manage`, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      const { template_mapping } = response;

      const templateMappingList = Object.entries(template_mapping).map(
        ([phase, phaseTemplates]) => ({
          encounter_phase: phase,
          templates: Object.entries(phaseTemplates)
            .filter(([_, template]) => template.enabled)
            .map(([template_id, { name }]) => ({
              template_id,
              name,
            })),
        })
      );

      dispatch(templateAdminListSlice(templateMappingList));
    } else {
      showToastError('Failed to load templates');
    }

    dispatch(templateAdminListLoadingSlice(false));
  };
};

export const putSuperAdminPreBuiltAdminTemplates = (
  template_mapping,
  encounter_phase,
  org_uuid
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      'Impersonate-Org-UUID': `${org_uuid}`,
      'Headless-Mode': true,
    };
    const data = {
      template_mapping: template_mapping,
      encounter_phase: encounter_phase,
    };
    const result = await AxiosAmbient.put(
      `/superadmin/templates/manage`,
      data,
      {
        headers,
      }
    );
    const response = result?.data;
    if (result?.status === 200) {
      showToastSuccess('Templates updated successfully');
    } else {
      showToastError('Failed to update templates');
    }
  };
};

export const postCreateEncountersOnBehalfOf = (payload) => {
  return async (dispatch) => {
    try {
      dispatch(createEncountersOnBehalfOfLoadingSlice(true));
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };

      const result = await Axios.post(
        `/v2/create-encounters-on-behalf`,
        payload,
        {
          headers,
        }
      );
      if (result?.status === 201) {
        dispatch(
          createEncountersOnBehalfOfSlice(
            result?.data?.create_encounters_on_behalf
          )
        );
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        showToastError(
          'You are not authorized to perform this action. User is not a clinician.'
        );
      } else {
        showToastError('Error occurred');
      }
      dispatch(createEncountersOnBehalfOfSlice(false));
    } finally {
      dispatch(createEncountersOnBehalfOfLoadingSlice(false));
    }
  };
};

export const getCreateEncountersOnBehalfOf = () => {
  return async (dispatch) => {
    try {
      dispatch(createEncountersOnBehalfOfLoadingSlice(true));
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };

      const result = await Axios.get(`/v2/create-encounters-on-behalf`, {
        headers,
      });
      if (result?.status === 200) {
        dispatch(
          createEncountersOnBehalfOfSlice(
            result?.data?.create_encounters_on_behalf
          )
        );
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        dispatch(
          postCreateEncountersOnBehalfOf({
            create_encounters_on_behalf: true,
          })
        );
      }
    } finally {
      dispatch(createEncountersOnBehalfOfLoadingSlice(false));
    }
  };
};

export const updateCreateEncountersOnBehalfOf = (payload) => {
  return async (dispatch) => {
    try {
      dispatch(createEncountersOnBehalfOfLoadingSlice(true));
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };

      const result = await Axios.put(
        `/v2/create-encounters-on-behalf`,
        payload,
        {
          headers,
        }
      );
      if (result?.status === 200) {
        dispatch(
          createEncountersOnBehalfOfSlice(
            result?.data?.create_encounters_on_behalf
          )
        );
        showToastSuccess('Setting updated');
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        showToastError(
          'You are not authorized to perform this action. User is not a clinician.'
        );
      } else {
        showToastError('Error occurred');
      }
      dispatch(createEncountersOnBehalfOfSlice(false));
    } finally {
      dispatch(createEncountersOnBehalfOfLoadingSlice(false));
    }
  };
};

export const getAllPreBuiltSuperAdminTemplatesByPlan = (org_uuid) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      'Impersonate-Org-UUID': `${org_uuid}`,
      'Headless-Mode': true,
    };
    const result = await AxiosAmbient.get(`/superadmin/templates/manage/plan`, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(templateAdminPlanListSlice(response.pre_built_templates));
    } else {
      showToastError('Failed to load templates');
    }
  };
};
